<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router"; // Add router imports
import useGlobalStateStore, { Enum_Theme } from "@/Store/GlobalStateStore";
import InfoBox_Left from "@/components/InfoBox_Left.vue";
import AnnouncementModal from "@/components/AnnouncementModal.vue";
import { AnOutlinedBilibili, AnOutlinedX, MdEmail, SiSinaweibo, CoBrandYoutube, AnOutlinedClose } from "@kalimahapps/vue-icons";
import { fetchAnnouncements } from "@/server/api";

const GlobalStyleStore = useGlobalStateStore();
const activeSection = ref(0);
const scrollContainer = ref<HTMLElement | null>(null);

const route = useRoute();
const router = useRouter();

const isModalVisible = ref(false);
const currentAnnouncementData = ref<any>(null);

function openAnnouncementModal(item: any) {
  currentAnnouncementData.value = item;
  isModalVisible.value = true;

  // Sync the news ID to the URL
  router.replace({ query: { ...route.query, news_id: item.id } });
}

function closeAnnouncementModal() {
  isModalVisible.value = false;

  // Remove the news_id param from the URL
  const currentQuery = { ...route.query };
  delete currentQuery.news_id;
  router.replace({ query: currentQuery });
}

const ThemeTopBarBackgroundColor = ref("transparent");
const ThemeTopBarColor = ref("#fff");
const ThemeItemColor = ref("#57E1D9");
const ThemeMainSloganColor = ref("#FFF");
const ThemeMainSloganCruxColor = ref("#57E1D9");
const ThemeSubSlogan = ref("#FFF");
const ThemeIconBackgroundColor = ref("#3fb7b0");
const ThemeIconBackgroundHoverColor = ref("#57E1D9");
const ThemeBottomBG = ref("#242424");
const ThemeBottomPartingLine = ref("#FFFFFF26");
const ThemeCopyrightFiling = ref("#FFF");
const ThemeHintColor = ref("#FFF");

// --- Home 3D Parallax Logic ---
const mouseX = ref(0);
const mouseY = ref(0);
const isHoveringHome = ref(false);

function onHomeMouseMove(e: MouseEvent) {
  isHoveringHome.value = true;
  const { innerWidth, innerHeight } = window;
  mouseX.value = (e.clientX / innerWidth - 0.5) * 2;
  mouseY.value = (e.clientY / innerHeight - 0.5) * 2;
}

function onHomeMouseLeave() {
  isHoveringHome.value = false;
  mouseX.value = 0;
  mouseY.value = 0;
}

const homeBgLayers = computed(() => {
  const moveX = mouseX.value;
  const moveY = mouseY.value;

  const config = [
    { mult: 0.4, scale: 1.02, opacity: 0.35, blur: '6px', blend: 'normal', hue: 0 },
    { mult: 0.8, scale: 1.06, opacity: 0.50, blur: '3px', blend: 'normal', hue: 15 },
    { mult: 1.3, scale: 1.10, opacity: 0.90, blur: '0px', blend: 'normal', hue: 0 },
    { mult: 2.0, scale: 1.16, opacity: 0.40, blur: '2px', blend: 'screen', hue: -15 },
    { mult: 2.8, scale: 1.25, opacity: 0.25, blur: '10px', blend: 'color-dodge', hue: 30 }
  ];

  return config.map((layer, index) => {
    const x = moveX * layer.mult * 1.5;
    const y = moveY * layer.mult * 1.5;
    return {
      transform: `scale(${layer.scale}) translate(${-x}%, ${-y}%)`,
      transition: isHoveringHome.value ? 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'transform 2.5s ease-out',
      backgroundImage: "url('/Images/HomeBG.jpg')",
      opacity: layer.opacity,
      filter: `blur(${layer.blur}) hue-rotate(${layer.hue}deg)`,
      mixBlendMode: layer.blend as any,
      zIndex: index + 1
    };
  });
});

// --- Section 3: "About Us" Data (English Translated) ---
const activeAboutTab = ref(0);
const aboutTabsData = [
  {
    num: "01",
    title: "General",
    subTitle: "GENERAL",
    paragraphs: [
      { label: "Company Profile", desc: "Founded in 2021, LESJEUXA is a next-generation game creative company. Built on the pillars of \"Technology R&D, Art Creation, and New Culture Communication,\" we are committed to providing outstanding and innovative \"Lesjeuxa Solutions\" for Asia and the world." },
      { label: "Purpose & Mission", desc: "Purpose: Supported by cutting-edge technology, we stimulate cultural vitality through artistic creativity, promoting cross-border integration of the gaming industry and cultural exchange.\nMission: We drive product development with technological innovation, convey human values through artistic expression, and disseminate globally influential new culture via digital media and multi-platform networks." },
      { label: "Vision", desc: "To continuously emerge as a pioneering enterprise leading global game creativity and cultural communication in every new era, achieving a perfect blend of technology and art, building an open, shared, and co-creative digital ecosystem, and fulfilling humanity's dream of a true metaverse and digital life." }
    ]
  },
  {
    num: "02",
    title: "Creed",
    subTitle: "CREED",
    paragraphs: [
      { label: "Technology R&D", desc: "We adhere to technology as our guide, constantly exploring new technologies and methods. We strive for breakthroughs in areas like AI, blockchain, and virtual reality to drive innovation in gaming and the metaverse. We focus on the commercialization and practical application of R&D results to provide safe, smooth, and innovative \"Lesjeuxa Concepts\" to global users." },
      { label: "Art Creation", desc: "We respect artistic diversity, stimulate creative potential, and encourage cross-border collaboration to integrate traditional culture with modern art and game design. Upholding the spirit of originality, we craft uniquely styled game worlds and visual expressions to deliver a distinctive aesthetic experience." },
      { label: "New Culture Communication", desc: "With an open and inclusive attitude, we promote cultural exchange by disseminating new cultural concepts that align with the spirit of the times. Leveraging digital media and social networks, we enable global users to share high-quality cultural content and innovative experiences." }
    ]
  },
  {
    num: "03",
    title: "Action & Mgmt",
    subTitle: "ACTION",
    paragraphs: [
      { label: "Innovation-Driven", desc: "We establish interdisciplinary, cross-domain creative teams to encourage deep integration of technology and art, crafting game products and cultural projects that align with future trends. We are enthusiastic about all forms of creative exchanges and industry seminars to inspire team creativity and collaborative spirit." },
      { label: "Institutional Guarantee", desc: "Our project management is executed in accordance with rules drafted based on the common law system, the most transparent system in human history. We establish comprehensive standards for R&D, creation, and communication to ensure efficient and standardized operations. We set up incentive and feedback mechanisms to encourage members to innovate and excel, creating a positive corporate culture." },
      { label: "Market & Cooperation", desc: "We actively expand and study domestic and international markets, building strategic partnerships, and joining hands with excellent enterprises and institutions to enter Gaming 2.0. We place great emphasis on brand building, creating a leading global image for Lesjeuxa in gaming culture and the metaverse through continuous innovation and premium service." }
    ]
  }
];

watch(() => [GlobalStyleStore.CurrentTheme, activeSection.value], () => {
  const isDark = GlobalStyleStore.CurrentTheme === Enum_Theme.Dark;
  const isHome = activeSection.value === 0;

  ThemeTopBarBackgroundColor.value = isHome ? 'transparent' : (isDark ? 'rgba(28, 28, 28, 0.57)' : 'rgba(255, 255, 255, 0.57)');
  ThemeTopBarColor.value = isDark ? '#fff' : '#484848';
  ThemeItemColor.value = isDark ? '#57E1D9' : '#f77373';

  if (isDark) {
    ThemeMainSloganColor.value = '#FFF';
    ThemeMainSloganCruxColor.value = '#57E1D9';
    ThemeSubSlogan.value = '#FFF';
    ThemeIconBackgroundColor.value = '#3fb7b0';
    ThemeIconBackgroundHoverColor.value = '#57E1D9';
    ThemeBottomBG.value = '#242424';
    ThemeBottomPartingLine.value = '#FFFFFF26';
    ThemeCopyrightFiling.value = 'rgba(255,255,255,0.6)';
    ThemeHintColor.value = 'rgba(255,255,255,0.6)';
  } else {
    ThemeMainSloganColor.value = '#545454';
    ThemeMainSloganCruxColor.value = '#f77373';
    ThemeSubSlogan.value = '#1a1a1a';
    ThemeIconBackgroundColor.value = '#de5959';
    ThemeIconBackgroundHoverColor.value = '#f77373';
    ThemeBottomBG.value = '#efefef';
    ThemeBottomPartingLine.value = 'rgba(185,185,185,0.1)';
    ThemeCopyrightFiling.value = 'rgba(0,0,0,0.6)';
    ThemeHintColor.value = 'rgba(0,0,0,0.6)';
  }
}, { immediate: true });

function scrollToSection(index: number) {
  if (scrollContainer.value) {
    const section = scrollContainer.value.querySelector(`#section-${index}`);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  }
}

let observer: IntersectionObserver | null = null;
const allAnnouncements = ref<any[]>([]);
const topAnnouncements = ref<any[]>([]);

onMounted(async () => {
  const options = { root: scrollContainer.value, rootMargin: '0px', threshold: 0.5 };
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const idParts = entry.target.id.split('-');
        if (idParts.length === 2) {
          activeSection.value = parseInt(idParts[1]);
        }
      }
    });
  }, options);
  document.querySelectorAll('.full-page-section').forEach(sec => observer?.observe(sec));

  const rawData = await fetchAnnouncements();
  const enList: any[] = [];

  rawData.forEach((item: any) => {
    try {
      // NOTE: 解析后端 JSON 数据时，提取对应的 .en 字段
      const titles = item.title.startsWith('{') ? JSON.parse(item.title) : { en: item.title };
      const summaries = item.summary.startsWith('{') ? JSON.parse(item.summary) : { en: item.summary };
      const contents = item.content.startsWith('{') ? JSON.parse(item.content) : { en: item.content };
      const authors = item.author && item.author.startsWith('{') ? JSON.parse(item.author) : { en: item.author || '' };

      if(titles.en) {
        enList.push({
          ...item,
          title: titles.en,
          summary: summaries.en || '',
          author: authors.en || '',
          content: contents.en || ''
        });
      }
    } catch(error) {
      console.warn("JSON parsing failed, possible dirty data", error);
    }
  });

  allAnnouncements.value = enList;
  topAnnouncements.value = enList.slice(0, 3);

  // Check URL parameters for direct link to news
  const newsId = route.query.news_id;
  if (newsId) {
    const targetNews = allAnnouncements.value.find(item => String(item.id) === String(newsId));
    if (targetNews) {
      // Delay to ensure DOM is mounted, then scroll and open modal
      setTimeout(() => {
        activeSection.value = 2; // Force active state on Navigation
        scrollToSection(2);      // Scroll down to News Section
        openAnnouncementModal(targetNews);
      }, 500);
    }
  }
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});

const isAllListModalVisible = ref(false);

const gameList = ref([
  {
    id: 1,
    titleCHS: 'Star Reverie · Greystasis',
    titleEN: '',
    desc: [
      'As Greystasis spreads and Oneiroth Syndrome runs rampant, humanity’s sanctuary is rapidly collapsing.',
      'By a twist of fate, the protagonist joins Nyctophoros and becomes a Covenant Keeper. While working with companions to resolve crises, help others, and uncover the truth of their own origins, they quietly draw closer to the secrets of the world.',
      'However, when the Dream Source—once a symbol of hope—is tainted by crimson, and nightmares engulf reality like a rising tide, time is running out. The vessel bearing fate and dreams teeters on the verge of shattering, and the blood of the gods will finally pour upon the mortal world...'
    ],
    highlight: 'Reboot, or reach the end.',
    kvPath: '/Images/Games/Star Reverie/StarReverieKV.png',
    iconPath: '/Images/Games/Star Reverie/StarReverieIcon.png'
  }
]);
const currentGameIndex = ref(0);

function OnUserClickLogo() {
  if (GlobalStyleStore.CurrentTheme === Enum_Theme.Dark) {
    GlobalStyleStore.SetCurrentTheme(Enum_Theme.Light);
  } else {
    GlobalStyleStore.SetCurrentTheme(Enum_Theme.Dark);
  }
}

async function OnCLickEmail() {
  try {
    await navigator.clipboard.writeText("info@lesjeuxa.com");
    alert("Email address copied to clipboard. We look forward to hearing from you! (๑′ᴗ‵๑)❤");
  } catch (error) {
    console.error("Failed to copy", error);
  }
}

function OnCLickX() {
  window.open("https://x.com", "_blank");
}

function OnCLickWeiBo() {
  window.open("https://weibo.com/", "_blank");
}

function OnCLickBilibili() {
  window.open("https://www.bilibili.com/", "_blank");
}

function OnCLickYoutuber() {
  window.open("https://www.youtube.com/@lesjeuxa", "_blank");
}
</script>

<template>
  <div class="MainPageBackground">

    <div class="TopBar">
      <div class="TopBarContent">
        <div class="Logo">
          <img src="/Images/Logo.png" alt="Logo" v-if="GlobalStyleStore.CurrentTheme===Enum_Theme.Dark" @click="OnUserClickLogo">
          <img src="/Images/Logo_1.png" alt="Logo" v-if="GlobalStyleStore.CurrentTheme===Enum_Theme.Light" @click="OnUserClickLogo">
        </div>
        <div class="NavBar" :class="{ 'hide-on-home': activeSection === 0 }">
          <span v-for="(item, index) in ['Home', 'Products', 'News', 'About Us', 'Join Us']" :key="index" class="nav-item" :class="{ active: activeSection === index }" @click="scrollToSection(index)">{{ item }}</span>
        </div>
        <div class="Login" :class="{ 'hide-on-home': activeSection === 0 }"><span class="item">Register</span><span class="separator">|</span><span class="item">Login</span></div>
      </div>
    </div>

    <div class="ScrollContainer" ref="scrollContainer">

      <!-- Section 0: Home -->
      <section
          id="section-0"
          class="full-page-section home-section"
          @mousemove="onHomeMouseMove"
          @mouseleave="onHomeMouseLeave"
      >
        <div
            v-for="(layerStyle, idx) in homeBgLayers"
            :key="idx"
            class="home-parallax-bg"
            :style="layerStyle"
        ></div>
        <div class="home-vignette-overlay"></div>
        <div class="home-particles">
          <div class="particle" v-for="n in 60" :key="n"></div>
        </div>
        <div class="overlay" style="z-index: 10;"></div>
        <div class="scroll-hint" style="z-index: 11; cursor: pointer;" @click="scrollToSection(1)">
          <p>Scroll down for more</p>
          <div class="arrow-down"></div>
        </div>
      </section>

      <!-- Section 1: Products -->
      <section id="section-1" class="full-page-section content-section">
        <div class="product-container" :class="{ 'is-active': activeSection === 1 }">
          <div class="decorative-sidebar"><span class="dot"></span> {{ gameList[currentGameIndex].titleEN.toUpperCase() }} <span class="dot"></span> LESJEUXA</div>
          <div class="product-left"><div class="product-image-wrapper"><img :src="gameList[currentGameIndex].kvPath" class="product-kv" :key="currentGameIndex" /></div></div>
          <div class="product-right">
            <h1 class="game-title">{{ gameList[currentGameIndex].titleCHS }}</h1>
            <h3 class="game-subtitle">{{ gameList[currentGameIndex].titleEN }}</h3>
            <div class="game-desc"><p v-for="(paragraph, i) in gameList[currentGameIndex].desc" :key="i">{{ paragraph }}</p><p class="highlight">{{ gameList[currentGameIndex].highlight }}</p></div>
          </div>
          <div class="game-list-sidebar">
            <div v-for="(game, index) in gameList" :key="game.id" class="game-list-item" :class="{ 'is-selected': currentGameIndex === index }" @click="currentGameIndex = index"><img :src="game.iconPath" /></div>
          </div>
        </div>
      </section>

      <!-- Section 2: News -->
      <section id="section-2" class="full-page-section content-section" :class="{ 'is-active': activeSection === 2 }">
        <div class="info-boxes-wrapper" v-if="topAnnouncements.length > 0">
          <div
              v-for="(item, index) in topAnnouncements"
              :key="item.id"
              class="anim-item"
              :style="{ 'transition-delay': `${index * 0.2}s` }"
          >
            <InfoBox_Left
                :Title="item.title"
                :Summary="item.summary"
                :ImagePath="item.cover_image"
                @openModal="openAnnouncementModal(item)"
            />
          </div>
        </div>
        <div class="info-boxes-wrapper empty-announcement" v-else><p>No news updates</p></div>
        <div class="more-btn" v-if="allAnnouncements.length > 3" @click="isAllListModalVisible = true">
          More <span>+</span>
        </div>
      </section>

      <!-- Section 3: About Us -->
      <section id="section-3" class="full-page-section content-section learn-us-section" :class="{ 'is-active': activeSection === 3 }">

        <div class="about-header-slogan">
          <h1 class="brand-core-slogan">
            <span class="highlight-cyan">Tech </span> R&D •
            <span class="highlight-cyan">Art</span> Creation •
            <span class="highlight-cyan">New Culture</span> Communication
          </h1>
          <p class="brand-core-desc">
            LESJEUXA is always committed to the integration of cutting-edge technology and artistic aesthetics, providing outstanding "Lesjeuxa Solutions" for the digital era.
          </p>
        </div>

        <div class="about-panel-container">
          <!-- Left: Vertical Tab Control Panel -->
          <div class="about-navigation">
            <div
                v-for="(tab, index) in aboutTabsData"
                :key="index"
                class="about-nav-card"
                :class="{ 'is-active': activeAboutTab === index }"
                @click="activeAboutTab = index"
            >
              <span class="card-num">{{ tab.num }}</span>
              <div class="card-title-group">
                <span class="card-title">{{ tab.title }}</span>
                <span class="card-subtitle">{{ tab.subTitle }}</span>
              </div>
              <div class="card-glow-bar"></div>
            </div>
          </div>

          <!-- Right: Details Content -->
          <div class="about-details-content">
            <div class="details-viewport" :key="activeAboutTab">
              <h2 class="viewport-main-title">{{ aboutTabsData[activeAboutTab].title }} Outline</h2>
              <div class="viewport-divider"></div>

              <div
                  v-for="(para, pIdx) in aboutTabsData[activeAboutTab].paragraphs"
                  :key="pIdx"
                  class="content-row"
                  :style="{ 'animation-delay': `${pIdx * 0.15}s` }"
              >
                <div class="row-header">
                  <span class="row-indicator"></span>
                  <h4 class="row-title">{{ para.label }}</h4>
                </div>
                <p class="row-description">{{ para.desc }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Section 4: Join Us -->
      <section id="section-4" class="full-page-section end-section">
        <div class="join-content">
          <div class="join-text-group"><h2 class="join-title">JOIN US</h2><p class="join-subtitle">Looking forward to your talent to weave the next dream together with us.</p></div>
          <div class="contact-wrapper" @click="OnCLickEmail">
            <div class="contact-label">MAIL TO US</div><div class="contact-email">info@lesjeuxa.com</div><div class="contact-hover-line"></div><div class="contact-hint">CLICK TO COPY EMAIL ADDRESS</div>
          </div>
        </div>
        <div class="BottomBG">
          <div class="BottomPartingLine"></div><div class="BottomContent"><div class="CopyrightFiling">Copyright 2021-2026 LESJEUXA.COM. All Rights Reserved.</div></div>
        </div>
      </section>
    </div>

    <!-- Modal 1: Read Announcement -->
    <AnnouncementModal :visible="isModalVisible" :announcement="currentAnnouncementData" @close="closeAnnouncementModal"/>

    <!-- Modal 2: All News List -->
    <transition name="fade">
      <div class="all-list-overlay" v-if="isAllListModalVisible">
        <div class="all-list-box">
          <button class="close-btn" @click="isAllListModalVisible = false"><AnOutlinedClose /></button>
          <h2 style="color: black; margin-bottom: 30px;">All News</h2>
          <div class="all-list-scroll">
            <div class="simple-list-item" v-for="item in allAnnouncements" :key="item.id" @click="openAnnouncementModal(item)">
              <img :src="item.cover_image" class="slim-cover" />
              <div class="simple-info">
                <h3>{{ item.title }}</h3>
                <p>{{ item.summary }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Global Scroll Hint -->
    <div
        class="global-scroll-hint"
        v-show="activeSection > 0 && activeSection < 4"
        @click="scrollToSection(activeSection + 1)"
    >
      <div class="arrow-down"></div>
    </div>

  </div>
</template>

<style scoped lang="scss">
/* ====== 动画与排版基建 ====== */
.anim-item { width: 100%; opacity: 0; transform: translateX(120px); transition: all 0.8s cubic-bezier(0.25, 0.8, 0.25, 1); }
.is-active .anim-item { opacity: 1; transform: translateX(0); }

.MainPageBackground { width: 100%; height: 100vh; overflow: hidden; display: flex; align-items: center; flex-direction: column; }
.TopBar { position: fixed; top: 0; width: 100%; background-color: v-bind(ThemeTopBarBackgroundColor); box-shadow: v-bind('activeSection === 0 ? "none" : "0 2px 4px rgba(0, 0, 0, 0.15)"'); color: v-bind(ThemeTopBarColor); display: flex; z-index: 100; backdrop-filter: blur(5px); transition: background-color 0.8s ease, box-shadow 0.8s ease; }
.TopBarContent { display: grid; width: 100%; height: 80px; grid-template-areas: "Logo NavBar Login"; grid-template-columns: 2fr 6fr 2fr; align-items: center; }
.Logo { grid-area: Logo; margin-left: 8%; cursor: pointer; user-select: none; }
.Logo img { max-width: 210px; }
.hide-on-home { opacity: 0; pointer-events: none; transform: translateY(-10px); }
.NavBar { grid-area: NavBar; display: flex; justify-content: center; gap: 3vw; transition: opacity 0.8s ease, transform 0.8s ease; }
.nav-item { font-family: 资源圆体, sans-serif; font-size: 1.1rem; font-weight: 500; color: v-bind(ThemeTopBarColor); cursor: pointer; position: relative; transition: color 0.4s ease; }
.nav-item.active, .nav-item:hover { color: v-bind(ThemeItemColor); font-weight: bold; }
.nav-item.active::after { content: ''; position: absolute; bottom: -5px; left: 20%; width: 60%; height: 2px; background-color: v-bind(ThemeItemColor); border-radius: 2px; }
.Login { grid-area: Login; display: flex; justify-content: flex-end; padding-right: 10%; user-select: none; transition: opacity 0.8s ease, transform 0.8s ease; }
.item { margin: 0 10px; font-family: 资源圆体, sans-serif; font-size: 1.1rem; font-weight: 500; color: v-bind(ThemeTopBarColor); transition: color 0.8s ease; cursor: pointer; }
.item:hover { color: v-bind(ThemeItemColor); }
.separator { margin: 0 5px; }

.ScrollContainer { width: 100vw; height: 100vh; overflow-y: scroll; scroll-snap-type: y mandatory; scroll-behavior: smooth; scrollbar-width: none; -ms-overflow-style: none; }
.ScrollContainer::-webkit-scrollbar { display: none; }
.full-page-section { width: 100vw; height: 100vh; scroll-snap-align: start; scroll-snap-stop: always; position: relative; display: flex; flex-direction: column; justify-content: center; align-items: center; }

.overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(15, 15, 15, 0.45); z-index: 10; pointer-events: none; }
.scroll-hint { position: absolute; bottom: 10%; display: flex; flex-direction: column; align-items: center; color: v-bind(ThemeHintColor); font-family: 资源圆体, sans-serif; letter-spacing: 2px; z-index: 11;cursor: pointer;}
.scroll-hint p { font-weight: bold; font-size: 1.1rem; margin: 0; }
.arrow-down { margin-top: 10px; width: 15px; height: 15px; border-right: 2px solid v-bind(ThemeHintColor); border-bottom: 2px solid v-bind(ThemeHintColor); transform: rotate(45deg); animation: arrow-bounce 2s infinite ease-in-out; }

/* 产品信息 */
.product-container { position: relative; width: 100vw; height: 100vh; display: flex; align-items: center; justify-content: flex-end; overflow: hidden; }
.decorative-sidebar { position: absolute; left: 2%; bottom: 12%; writing-mode: vertical-rl; transform: rotate(180deg); font-family: sans-serif; font-size: 0.75rem; font-weight: bold; letter-spacing: 6px; color: v-bind(ThemeHintColor); opacity: 0; transition: opacity 1.5s ease 1s; z-index: 10; display: flex; align-items: center; }
.decorative-sidebar .dot { display: inline-block; width: 4px; height: 4px; background-color: v-bind(ThemeMainSloganCruxColor); margin: 15px 0; border-radius: 50%; }
.is-active .decorative-sidebar { opacity: 0.4; }
.product-left { position: absolute; left: 0; top: 0; width: 60%; height: 100%; z-index: 1; -webkit-mask-image: linear-gradient(to right, rgba(0,0,0,1) 50%, rgba(0,0,0,0) 100%); mask-image: linear-gradient(to right, rgba(0,0,0,1) 50%, rgba(0,0,0,0) 100%); }
.product-image-wrapper { width: 100%; height: 100%; overflow: hidden; }
.product-kv { width: 100%; height: 100%; object-fit: cover; object-position: 30% center; opacity: 0; transform: scale(1.15) translateX(-8%); transition: transform 0.8s ease, opacity 0.8s ease; }
.is-active .product-kv { opacity: 1; transform: scale(1) translateX(0); transition: transform 5s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 1.5s ease; }
.product-right { position: relative; width: 45%; max-width: 800px; margin-right: 8%; padding-right: 90px; z-index: 2; display: flex; flex-direction: column; justify-content: center; }
.game-title, .game-subtitle, .game-desc { opacity: 0; transform: translateX(60px); transition: transform 0.6s ease, opacity 0.6s ease; }
.is-active .game-title { opacity: 1; transform: translateX(0); transition: transform 1.2s cubic-bezier(0.25, 0.8, 0.25, 1) 0.3s, opacity 1.2s ease 0.3s; }
.is-active .game-subtitle { opacity: 1; transform: translateX(0); transition: transform 1.2s cubic-bezier(0.25, 0.8, 0.25, 1) 0.5s, opacity 1.2s ease 0.5s; }
.is-active .game-desc { opacity: 1; transform: translateX(0); transition: transform 1.2s cubic-bezier(0.25, 0.8, 0.25, 1) 0.7s, opacity 1.2s ease 0.7s; }
.game-title { font-family: 资源圆体, sans-serif; font-size: 2.8rem; color: v-bind(ThemeMainSloganColor); margin: 0; }
.game-subtitle { font-family: sans-serif; font-size: 1.5rem; color: v-bind(ThemeMainSloganCruxColor); margin: 5px 0 30px 0; letter-spacing: 2px; }
.game-desc p { font-family: 资源圆体, sans-serif; color: v-bind(ThemeSubSlogan); line-height: 1.8; font-size: 1.05rem; margin-bottom: 12px; }
.game-desc .highlight { color: v-bind(ThemeMainSloganCruxColor); font-weight: bold; font-size: 1.2rem; margin-top: 25px; }
.game-list-sidebar { position: absolute; right: 3%; top: 50%; transform: translateY(-50%); display: flex; flex-direction: column; gap: 15px; z-index: 10; }
.game-list-item { width: 65px; height: 65px; border-radius: 12px; overflow: hidden; opacity: 0.4; transform: scale(0.8); cursor: pointer; border: 2px solid transparent; box-shadow: 0 4px 10px rgba(0,0,0,0.2); transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1); }
.game-list-item img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.4s ease; }
.game-list-item:hover { opacity: 0.8; transform: scale(0.9); }
.game-list-item.is-selected { opacity: 1; transform: scale(1); border-color: v-bind(ThemeMainSloganCruxColor); box-shadow: 0 0 15px rgba(87, 225, 217, 0.3); }
.game-list-item.is-selected img { transform: scale(1.1); }

/* 新闻 */
.info-boxes-wrapper { width: 100%; display: flex; flex-direction: column; align-items: center; }
.empty-announcement { font-family: 资源圆体, sans-serif; color: v-bind(ThemeSubSlogan); opacity: 0.6; }

/* 更多公告按钮 */
.more-btn { position: absolute; right: 8%; bottom: 12%; background: v-bind(ThemeIconBackgroundHoverColor); color: #111; font-weight: bold; font-family: sans-serif; padding: 10px 25px; border-radius: 30px; cursor: pointer; font-size: 1.1rem; box-shadow: 0 5px 15px rgba(0,0,0,0.2); transition: 0.3s; animation: float 3s infinite ease-in-out; z-index: 10; }
.more-btn:hover { transform: scale(1.05); }
@keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }

/* 查看所有列表的模态框 */
.all-list-overlay { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(255,255,255,0.9); backdrop-filter: blur(10px); z-index: 9998; display: flex; justify-content: center; align-items: center; }
.all-list-box { width: 90%; max-width: 800px; height: 80vh; background: transparent; display: flex; flex-direction: column; position: relative; }
.all-list-scroll { flex: 1; overflow-y: auto; padding-right: 15px; }
.simple-list-item { display: flex; background: white; border-radius: 12px; margin-bottom: 20px; padding: 20px; box-shadow: 0 5px 15px rgba(0,0,0,0.05); cursor: pointer; transition: 0.3s; border: 1px solid #eee; }
.simple-list-item:hover { transform: translateY(-3px); box-shadow: 0 10px 25px rgba(0,0,0,0.1); border-color: v-bind(ThemeMainSloganCruxColor); }
.slim-cover { width: 140px; height: 90px; object-fit: cover; border-radius: 8px; margin-right: 20px; }
.simple-info h3 { margin: 0 0 10px 0; color: #333; font-family: 资源圆体, sans-serif; }
.simple-info p { margin: 0; color: #777; font-size: 0.9rem; line-height: 1.5; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.close-btn { position: absolute; top: -10px; right: 0; background: rgba(0,0,0,0.1); border: none; width: 40px; height: 40px; border-radius: 50%; color: black; font-size: 20px; cursor: pointer; z-index: 10; transition: 0.3s; display: flex; align-items: center; justify-content: center; }
.close-btn:hover { background: #f77373; color: white;}

/* ================= 极具科技感的“了解我们” Section 3 重制样式 ================= */
.learn-us-section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  padding: 0 8%;
  box-sizing: border-box;
}

/* 顶部企业核心 Slogan 展示区 */
.about-header-slogan {
  display: flex;
  flex-direction: column;
  align-items: center;  /* 强行使所有子代块级元素在纵轴上居中对齐 */
  text-align: center;    /* 保证行内文本默认居中 */
  width: 100%;           /* 铺满整个屏幕宽度，消除偏左现象 */
  margin-bottom:4vh;
}

.is-active .about-header-slogan {
  opacity: 1;
  transform: translateY(0);
}

.brand-core-slogan {
  font-family: 资源圆体, sans-serif;
  /* 使用 clamp 让字体大小随屏幕宽度自适应缩放，防止大字号导致的溢出 */
  font-size: clamp(1.2rem, 3vw, 2.6rem);
  font-weight: 900;
  letter-spacing: 2px;
  color: v-bind(ThemeMainSloganColor);
  margin: 0 0 12px 0;

  /* 核心修复：强制单行显示，禁止换行 */
  white-space: nowrap;
  display: inline-block;
}

.brand-core-slogan .highlight-cyan {
  color: v-bind(ThemeMainSloganCruxColor);
  position: relative;
  display: inline-block;
}

.brand-core-desc {
  font-family: 资源圆体, sans-serif;
  font-size: 1.05rem;
  line-height: 1.6;
  color: v-bind(ThemeSubSlogan);
  opacity: 0.75;
  margin: 0 auto;
  max-width: 700px;
}

/* 下方：科技面板整体高度微调，给顶部 Slogan 留空 */
.about-panel-container {
  display: flex;
  width: 100%;
  max-width: 1200px;
  height: 52vh;
  gap: 5%;
  align-items: stretch;
  opacity: 0;
  transform: translateY(30px);
  transition: all 1.2s cubic-bezier(0.25, 0.8, 0.25, 1) 0.3s;
}

.is-active .about-panel-container {
  opacity: 1;
  transform: translateY(0);
}

/* 左侧：立体科技感垂直 Tab 控制板 */
.about-navigation {
  flex: 0 0 320px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
}

.about-nav-card {
  position: relative;
  display: flex;
  align-items: center;
  padding: 18px 22px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(150, 150, 150, 0.1);
  backdrop-filter: blur(10px);
  cursor: pointer;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.about-nav-card:hover {
  transform: translateX(10px);
  border-color: v-bind(ThemeMainSloganCruxColor);
  background: rgba(255, 255, 255, 0.05);
}

.card-num {
  font-family: sans-serif;
  font-size: 1.8rem;
  font-weight: 800;
  color: v-bind(ThemeHintColor);
  opacity: 0.3;
  margin-right: 18px;
  transition: all 0.4s ease;
}

.card-title-group {
  display: flex;
  flex-direction: column;
}

.card-title {
  font-family: 资源圆体, sans-serif;
  font-size: 1.2rem;
  font-weight: bold;
  color: v-bind(ThemeMainSloganColor);
  transition: color 0.4s ease;
}

.card-subtitle {
  font-family: sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 1px;
  color: v-bind(ThemeHintColor);
  opacity: 0.6;
}

/* 激活状态的立体光影效果 */
.about-nav-card.is-active {
  background: rgba(87, 225, 217, 0.08);
  border-color: v-bind(ThemeMainSloganCruxColor);
  box-shadow: 0 8px 25px rgba(87, 225, 217, 0.15), inset 0 0 15px rgba(87, 225, 217, 0.1);
  transform: translateX(15px);
}

.about-nav-card.is-active .card-num {
  color: v-bind(ThemeMainSloganCruxColor);
  opacity: 1;
  transform: scale(1.1);
}

.about-nav-card.is-active .card-title {
  color: v-bind(ThemeMainSloganCruxColor);
}

/* 激活时卡片侧边的光感流线 */
.card-glow-bar {
  position: absolute;
  left: 0;
  top: 0;
  width: 4px;
  height: 100%;
  background: v-bind(ThemeMainSloganCruxColor);
  transform: scaleY(0);
  transition: transform 0.4s ease;
}
.about-nav-card.is-active .card-glow-bar {
  transform: scaleY(1);
}


/* 右侧：量子扫描式的正文框 */
.about-details-content {
  flex: 1;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(150, 150, 150, 0.08);
  border-radius: 20px;
  padding: 35px 45px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(15px);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.details-viewport {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
}

.viewport-main-title {
  font-family: 资源圆体, sans-serif;
  font-size: 1.6rem;
  color: v-bind(ThemeMainSloganColor);
  margin: 0 0 12px 0;
  letter-spacing: 1px;
}

.viewport-divider {
  width: 50px;
  height: 3px;
  background: v-bind(ThemeMainSloganCruxColor);
  margin-bottom: 25px;
  border-radius: 2px;
}

/* 每一行内容的流畅滑入动画 */
.content-row {
  margin-bottom: 22px;
  opacity: 0;
  transform: translateY(25px);
  animation: slide-up-reveal 0.6s cubic-bezier(0.25, 1, 0.5, 1) forwards;
}

.row-header {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.row-indicator {
  display: inline-block;
  width: 6px;
  height: 6px;
  background-color: v-bind(ThemeMainSloganCruxColor);
  border-radius: 50%;
  margin-right: 12px;
}

.row-title {
  font-family: 资源圆体, sans-serif;
  font-size: 1.1rem;
  font-weight: bold;
  color: v-bind(ThemeMainSloganColor);
  margin: 0;
}

.row-description {
  font-family: 资源圆体, sans-serif;
  font-size: 0.92rem;
  line-height: 1.65;
  color: v-bind(ThemeSubSlogan);
  opacity: 0.85;
  margin: 0;
  padding-left: 18px;
  white-space: pre-line;
}

@keyframes slide-up-reveal {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 移动端适配 */
@media screen and (max-width: 900px) {
  .brand-core-slogan { font-size: 1.8rem; }
  .brand-core-desc { font-size: 0.9rem; }
  .about-panel-container {
    flex-direction: column;
    height: auto;
    gap: 20px;
  }
  .about-navigation {
    flex: 0 0 auto;
    flex-direction: row;
    width: 100%;
    flex-wrap: wrap;
    gap: 10px;
  }
  .about-nav-card {
    flex: 1 1 30%;
    padding: 10px 12px;
    justify-content: center;
  }
  .about-nav-card.is-active {
    transform: translateY(-5px);
  }
  .about-details-content {
    padding: 20px;
    height: 40vh;
  }
  .viewport-main-title {
    font-size: 1.3rem;
  }
}
@media screen and (max-width: 480px) {
  .brand-core-slogan { font-size: 1.4rem; }
  .about-nav-card {
    flex: 1 1 100%;
  }
}
/* ======================================================================== */

/* 底部与卡片 */
.end-section { justify-content: space-between; }
.join-content { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; width: 100%; padding: 0 20px; }
.join-text-group { text-align: center; margin-bottom: 50px; }
.join-title { font-family: 资源圆体, sans-serif; font-size: 3rem; color: v-bind(ThemeMainSloganColor); margin: 0 0 10px 0; letter-spacing: 2px; }
.join-title .highlight-text { color: v-bind(ThemeMainSloganCruxColor); }
.join-subtitle { font-family: 资源圆体, sans-serif; font-size: 1.1rem; color: v-bind(ThemeSubSlogan); opacity: 0.8; letter-spacing: 1px; margin: 0; }
.contact-wrapper { position: relative; display: flex; flex-direction: column; align-items: center; padding: 40px 60px; border: 1px solid rgba(150, 150, 150, 0.2); border-radius: 16px; cursor: pointer; background: rgba(255, 255, 255, 0.02); backdrop-filter: blur(10px); transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1); overflow: hidden; }
.contact-wrapper::before { content: ''; position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: radial-gradient(circle at center, v-bind(ThemeMainSloganCruxColor) 0%, transparent 70%); opacity: 0; transition: opacity 0.5s ease; z-index: 0; mix-blend-mode: overlay; }
.contact-wrapper:hover { transform: translateY(-5px) scale(1.02); border-color: v-bind(ThemeMainSloganCruxColor); box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2), 0 0 20px rgba(87, 225, 217, 0.1); }
.contact-wrapper:hover::before { opacity: 0.15; }
.contact-label { position: relative; font-family: sans-serif; font-size: 0.85rem; font-weight: bold; letter-spacing: 4px; color: v-bind(ThemeMainSloganCruxColor); margin-bottom: 15px; z-index: 1; }
.contact-email { position: relative; font-family: sans-serif; font-size: 3.5rem; font-weight: 900; color: v-bind(ThemeMainSloganColor); letter-spacing: 2px; margin-bottom: 10px; z-index: 1; transition: color 0.4s ease; }
.contact-wrapper:hover .contact-email { color: v-bind(ThemeMainSloganCruxColor); }
.contact-hover-line { position: relative; width: 0; height: 3px; background-color: v-bind(ThemeMainSloganCruxColor); transition: width 0.5s cubic-bezier(0.25, 0.8, 0.25, 1); z-index: 1; }
.contact-wrapper:hover .contact-hover-line { width: 100%; }
.contact-hint { position: relative; margin-top: 20px; font-family: 资源圆体, sans-serif; font-size: 0.9rem; color: v-bind(ThemeHintColor); opacity: 0; transform: translateY(10px); transition: all 0.4s ease; z-index: 1; }
.contact-wrapper:hover .contact-hint { opacity: 0.7; transform: translateY(0); }
.BottomBG { width: 100%; background: v-bind(ThemeBottomBG); }
.BottomPartingLine { background-color: v-bind(ThemeBottomPartingLine); height: 2px; width: 100%; }
.CopyrightFiling { color: v-bind(ThemeCopyrightFiling); font-family: 资源圆体, sans-serif; margin: 15px 0; text-align: center; font-size: 0.9rem; }

/* 移动端适配 */
@media screen and (max-width: 1024px) {
  .TopBarContent { grid-template-columns: 1fr 7fr 2fr; } .NavBar { gap: 1.5vw; } .nav-item { font-size: 0.95rem; } .Logo { margin-left: 2%; } .Logo img { max-width: 150px; } .product-right { width: 55%; padding-right: 80px; margin-right: 5%; } .game-title { font-size: 2.2rem; } .game-subtitle { font-size: 1.2rem; } .game-list-item { width: 55px; height: 55px; } .join-title { font-size: 2.5rem; } .contact-email { font-size: 2.8rem; } .contact-wrapper { padding: 30px 40px; }
}
@media screen and (max-width: 768px) {
  .TopBarContent { display: flex; justify-content: space-between; padding: 0 15px; } .Logo img { max-width: 120px; } .NavBar { display: none; } .Login { padding-right: 0; } .item { font-size: 0.9rem; margin: 0 5px; } .CopyrightFiling { font-size: 0.75rem; }
  .decorative-sidebar { display: none; } .product-container { flex-direction: column; justify-content: flex-end; padding-bottom: 15vh; } .product-left { width: 100%; height: 60%; top: 0; -webkit-mask-image: linear-gradient(to bottom, rgba(0,0,0,1) 40%, rgba(0,0,0,0) 100%); mask-image: linear-gradient(to bottom, rgba(0,0,0,1) 40%, rgba(0,0,0,0) 100%); } .product-right { width: 90%; margin-right: 0; padding: 0; text-align: center; } .game-list-sidebar { top: auto; bottom: 3vh; right: 50%; transform: translateX(50%); flex-direction: row; gap: 12px; } .game-list-item { width: 45px; height: 45px; } .game-title { font-size: 1.8rem; } .game-desc p { font-size: 0.95rem; } .join-title { font-size: 2rem; } .join-subtitle { font-size: 0.9rem; } .contact-email { font-size: 1.8rem; } .contact-wrapper { padding: 25px 20px; width: 90%; } .contact-label { font-size: 0.75rem; } .contact-hint { opacity: 0.7; transform: translateY(0); margin-top: 15px; }
  .more-btn { right: 5%; bottom: 5%; font-size: 0.9rem; padding: 8px 15px;}
}
@media screen and (max-width: 425px) {
  .game-desc p { font-size: 0.85rem; line-height: 1.5; margin-bottom: 8px;} .game-desc .highlight { font-size: 1rem; margin-top: 15px;} .contact-email { font-size: 1.3rem; letter-spacing: 1px; } .join-title { font-size: 1.6rem; }
}

/* 过渡动画 */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* 补充箭头动画 */
@keyframes arrow-bounce {
  0%, 100% { transform: translateY(0) rotate(45deg); }
  50% { transform: translateY(10px) rotate(45deg); }
}
/* 纯净版右下角滚动提示 */
.global-scroll-hint {
  position: fixed;
  right: 5%;
  bottom: 8%;
  z-index: 100;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  opacity: 0.5;
  transition: all 0.3s ease;
}

.global-scroll-hint:hover {
  opacity: 1;
  transform: scale(1.2);
}

.global-scroll-hint .arrow-down {
  margin-top: -8px;
}

/* ================= 首页特效专属样式 ================= */
.home-section {
  position: relative;
  overflow: hidden;
  background-color: #050b10;
}

.home-parallax-bg {
  position: absolute;
  top: -10%; left: -10%;
  width: 120%; height: 120%;
  background-size: cover;
  background-position: center;
  will-change: transform, filter;
}

.home-parallax-bg:nth-child(3) {
  animation: bg-breathe 20s infinite alternate ease-in-out;
}
@keyframes bg-breathe {
  0% { filter: brightness(0.9); }
  100% { filter: brightness(1.1) scale(1.03); }
}

.home-vignette-overlay {
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: radial-gradient(circle at center, transparent 20%, rgba(0, 0, 0, 0.75) 100%);
  z-index: 6;
  pointer-events: none;
}

.home-particles {
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  z-index: 7;
  pointer-events: none;
}

.particle {
  position: absolute;
  border-radius: 50%;
  animation: float-up linear infinite;
  opacity: 0;
}

/* 编译修正后的粒子循环 */
@for $i from 1 through 60 {
  .particle:nth-child(#{$i}) {
    $size: (random(6) + 1) * 1px;
    width: $size;
    height: $size;
    left: random(100) + vw;
    top: random(110) + vh;

    @if random(10) > 7 {
      background: rgba(255, 255, 255, 0.85);
      box-shadow: 0 0 12px rgba(255, 255, 255, 0.9), 0 0 20px rgba(87, 225, 217, 0.5);
    } @else {
      background: rgba(87, 225, 217, 0.8);
      box-shadow: 0 0 10px rgba(87, 225, 217, 0.8), 0 0 18px rgba(87, 225, 217, 0.3);
    }

    animation-duration: random(15) + 12 + s;
    animation-delay: -#{random(25)}s;

    @if $size > 5px {
      filter: blur(2px);
    } @else {
      filter: blur(0);
    }
  }
}

@keyframes float-up {
  0% { transform: translateY(0) translateX(0) scale(0.5); opacity: 0; }
  15% { opacity: 1; }
  85% { opacity: 0.8; }
  100% { transform: translateY(-40vh) translateX(random(30) - 15 + vw) scale(1.6); opacity: 0; }
}
/* ================================================= */
</style>
